import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import { Check, ChevronsUpDown, User } from "lucide-react";

export default function ConsumerCombobox({ consumers, value, onChange, recentIds = [] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = consumers.find((c) => c.id === value) || null;
  const label = selected ? `${selected.name}${selected.member_id ? ` · ${selected.member_id}` : ""}` : "No consumer — freestyle";

  // recent consumers that still exist, in most-recent-first order
  const recent = recentIds.map((id) => consumers.find((c) => c.id === id)).filter(Boolean).slice(0, 5);
  const showRecent = !query.trim() && recent.length > 0;

  const Item = ({ c }) => (
    <CommandItem
      key={c.id}
      value={`${c.name} ${c.member_id || ""}`}
      data-testid={`consumer-option-${c.id}`}
      onSelect={() => { onChange(c.id); setOpen(false); setQuery(""); }}
    >
      <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
      <span className="truncate">{c.name}{c.member_id ? <span className="text-[#7A8C82]"> · {c.member_id}</span> : null}</span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="consumer-select"
          type="button"
          className="flex items-center justify-between gap-2 h-12 w-full sm:max-w-sm rounded-md bg-[#F9F9F6] border border-[#E2DFD8] px-3 text-sm text-[#121814] hover:border-[#4A6B53] transition-colors"
        >
          <span className="flex items-center gap-2 truncate">
            <User className="w-4 h-4 text-[#7A8C82] shrink-0" strokeWidth={1.5} />
            <span className="truncate">{label}</span>
          </span>
          <ChevronsUpDown className="w-4 h-4 text-[#7A8C82] shrink-0" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command>
          <CommandInput data-testid="consumer-search-input" placeholder="Search clients by name or ID…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>No client found.</CommandEmpty>
            {showRecent && (
              <CommandGroup heading="Recently used">
                {recent.map((c) => <Item key={c.id} c={c} />)}
              </CommandGroup>
            )}
            <CommandGroup heading={showRecent ? "All clients" : undefined}>
              <CommandItem
                value="No consumer — freestyle"
                data-testid="consumer-option-none"
                onSelect={() => { onChange(""); setOpen(false); setQuery(""); }}
              >
                <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
                No consumer — freestyle
              </CommandItem>
              {consumers.map((c) => <Item key={c.id} c={c} />)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
